import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 5 generated empty Mayfair rooms. Each has the empty-wall coordinates
// detected by gpt-4o-mini vision so we know where to overlay the artwork.
const ROOMS = [
  {
    slug: '01_drawing_room',
    src: '/hero-rooms/01_drawing_room.jpg',
    wall: { top: 18, left: 36, width: 28, height: 42 },
    label: 'Drawing room',
  },
  {
    slug: '02_study',
    src: '/hero-rooms/02_study.jpg',
    wall: { top: 18, left: 36, width: 28, height: 42 },
    label: 'Private study',
  },
  {
    slug: '03_dining',
    src: '/hero-rooms/03_dining.jpg',
    wall: { top: 22, left: 30, width: 40, height: 56 },
    label: 'Dining room',
  },
  {
    slug: '04_master_bedroom',
    src: '/hero-rooms/04_master_bedroom.jpg',
    wall: { top: 18, left: 32, width: 36, height: 50 },
    label: 'Master bedroom',
  },
  {
    slug: '05_hallway',
    src: '/hero-rooms/05_hallway.jpg',
    wall: { top: 22, left: 30, width: 40, height: 50 },
    label: 'Entrance hallway',
  },
];

/**
 * Stable per-string hash so the same artist always picks the same 3 rooms.
 */
const hash = (s) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

/**
 * Pick `count` rooms from ROOMS deterministically by seed.
 */
const pickRooms = (seed, count = 3) => {
  const rng = hash(seed) || 1;
  const indices = ROOMS.map((_, i) => i).sort((a, b) =>
    ((rng * (a + 1)) % 7) - ((rng * (b + 1)) % 7)
  );
  return indices.slice(0, count).map((i) => ROOMS[i]);
};

/**
 * <ArtworkOnWall> — places a CSS-framed artwork into the empty wall area.
 * The wall coords define a max bounding box; the frame inside is sized to
 * the largest rectangle that fits inside while honouring the artwork's
 * natural aspect ratio (so the mat shows around all sides cleanly, no
 * cropping, no excess white space, no horizontal stretch).
 */
const ArtworkOnWall = ({ artwork, wall, tilt = 0 }) => {
  const [aspect, setAspect] = React.useState(null);
  const wallRef = React.useRef(null);
  const [size, setSize] = React.useState({ w: 0, h: 0 });

  // Measure the wall bounding box and recompute the frame size whenever
  // the room resizes or the artwork aspect resolves.
  React.useLayoutEffect(() => {
    const a = aspect || 4 / 5;
    const measure = () => {
      if (!wallRef.current) return;
      const r = wallRef.current.getBoundingClientRect();
      let fw = r.width;
      let fh = fw / a;
      if (fh > r.height) {
        fh = r.height;
        fw = fh * a;
      }
      setSize({ w: fw, h: fh });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wallRef.current) ro.observe(wallRef.current);
    return () => ro.disconnect();
  }, [aspect]);

  if (!artwork?.imageUrl) return null;
  const src = artwork.imageUrl.startsWith('http')
    ? artwork.imageUrl
    : `${API_URL}${artwork.imageUrl}`;

  return (
    <div
      ref={wallRef}
      className="absolute flex items-center justify-center"
      style={{
        top: `${wall.top}%`,
        left: `${wall.left}%`,
        width: `${wall.width}%`,
        height: `${wall.height}%`,
        transform: `perspective(1400px) rotateY(${tilt}deg)`,
        transformOrigin: 'center center',
      }}
    >
      {size.w > 0 && /canvas/i.test(artwork.medium || '') && (
        <img
          src={src}
          alt={artwork.title}
          onLoad={(e) => {
            const w = e.target.naturalWidth, h = e.target.naturalHeight;
            if (w && h) setAspect(w / h);
          }}
          className="block object-cover"
          style={{
            width: `${size.w}px`,
            height: `${size.h}px`,
            boxShadow: '0 24px 40px -18px rgba(0,0,0,0.55), 4px 6px 14px rgba(0,0,0,0.35)',
          }}
          data-testid="artist-hero-canvas"
          loading="lazy"
        />
      )}
      {size.w > 0 && !/canvas/i.test(artwork.medium || '') && (
        <div
          className="relative"
          style={{
            width: `${size.w}px`,
            height: `${size.h}px`,
            padding: '3.5%',
            background:
              'linear-gradient(135deg, #1c1c1c 0%, #2a2a2a 40%, #0e0e0e 100%)',
            boxShadow:
              '0 28px 48px -16px rgba(0,0,0,0.55), ' +
              '0 6px 14px rgba(0,0,0,0.35), ' +
              'inset 0 1px 0 rgba(255,255,255,0.08), ' +
              'inset 0 -1px 0 rgba(0,0,0,0.6)',
          }}
        >
          <div
            className="relative w-full h-full bg-[#f7f5ef] flex items-center justify-center"
            style={{
              padding: '7%',
              boxShadow:
                'inset 0 0 0 1px rgba(0,0,0,0.05), inset 0 6px 12px -6px rgba(0,0,0,0.05)',
            }}
          >
            <img
              src={src}
              alt={artwork.title}
              onLoad={(e) => {
                const w = e.target.naturalWidth, h = e.target.naturalHeight;
                if (w && h) setAspect(w / h);
              }}
              className="block w-full h-full object-cover"
              style={{
                boxShadow:
                  '0 0 0 1px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.16)',
              }}
              loading="lazy"
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'linear-gradient(115deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 100%)',
                mixBlendMode: 'screen',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * <ArtistHeroScene> — pick 1 of the 3 rooms assigned to this artist, render
 * it as a cinematic background with the artist's hero artwork CSS-overlaid.
 *
 * Props
 *   artist:  { id, name }
 *   artwork: { title, imageUrl } — the hero artwork
 *   sceneIndex: 0 | 1 | 2 — which of the assigned rooms to render
 */
const ArtistHeroScene = ({ artist, artwork, sceneIndex = 0, className = '' }) => {
  const rooms = useMemo(() => pickRooms(artist?.id || artist?.name || 'x', 3), [artist]);
  const room = rooms[Math.min(sceneIndex, rooms.length - 1)] || ROOMS[0];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className={`relative w-full overflow-hidden bg-neutral-950 ${className}`}
      data-testid="artist-hero-scene"
      data-room={room.slug}
    >
      {/* Background room */}
      <img
        src={room.src}
        alt={`${artist.name} scene — ${room.label}`}
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Slight gradient for legibility of any overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10 pointer-events-none" />

      {/* Artwork overlay */}
      <ArtworkOnWall artwork={artwork} wall={room.wall} tilt={0.3} />
    </motion.div>
  );
};

export default ArtistHeroScene;
export { ROOMS, pickRooms };
